import React, { useState, useEffect, Platform, useCallback, useRef } from "react";
import { View, Text, Pressable, TouchableOpacity, StyleSheet, Image, FlatList, TextInput, Dimensions, Picker, Alert, ScrollView, ActivityIndicator } from "react-native";
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { app } from "../../firebase";
import { useNavigation, useRoute } from '@react-navigation/native'; // Ensure you import useNavigation and useRoute
import HeaderBar, { useScrollHide } from "../../Widgets/HeaderBar";
import AnalyticsManager from "../../services/AnalyticsManager";
import { HEADER_PADDING, API_URL } from '../../constants';
import { ensureAffiliateTag, toRegistryCandidate } from '../../utils/affiliateUrl';

const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1500;
const isLargeDesktop = width > 1500;
const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

const VALID_SEARCH_INDICES = [
"Bedroom", "Bathroom", "Decor", "Health & Personal Care", "Kitchen", "Laundry & Cleaning", "Electronics & Office Supplies", "Storage & Organization"
];

const categoryImages = {
	"Bedroom": require("../../assets/Catalogue/Bedroom.jpg"),
	"Bathroom": require("../../assets/Catalogue/Bathroom.jpg"),
	"Decor": require("../../assets/Catalogue/Decor.jpg"),
	"Health & Personal Care": require("../../assets/Catalogue/Health & Personal Care.jpg"),
	"Kitchen": require("../../assets/Catalogue/Kitchen.jpg"),
	"Laundry & Cleaning": require("../../assets/Catalogue/Laundry & Cleaning.jpg"),
	"Electronics & Office Supplies": require("../../assets/Catalogue/Electronics & Office Supplies.jpg"),
	"Storage & Organization": require("../../assets/Catalogue/Storage & Organization.jpg"),
};

const subCategories = {
	"Bedroom": [
		"Sheets", "Comforter", "Bed In a Bag", "Pillow", "Pillow Protector", "Body Pillow", 
		"Mattress Topper", "Mattress Protector", "Throw Blanket", "Study Pillow", 
		"Space Heaters",
		"Misc"
	],
	"Bathroom": [
		"Towel Set", "Waterproof Speakers",
		"Shower Caddy", "Shower Shoes", "Bathrobe", "Shower Wrap", "Hair Wrap", 
		"Bathroom Rug", "Shower Curtain", "Tension Rod", "Cosmetic Organizer",
		"Misc"
	],
	"Laundry & Cleaning": [
		"Cleaning Bundle", "Laundry Basket", "Hamper", "Laundry Detergent", 
		"Odor Eliminator", "Steamer", "Iron",
		"Ironing Board", "Vacuums", "Laundry Bag", "Broom & Dust Pan", 
		"Garbage Can", "Misc"
	],
	"Electronics & Office Supplies": [
		"Television", "Laptop", "Chargers", "Flash Drive", "Hard Drive", 
		"Extension Cords", "Headphones", "Printer", "Alarm Clock", 
		"Bluetooth Speaker", "Personal Alarm", "Room Security",
		"Firestick", "Alexa/Amazon Speakers",
		"Day Planner", "Backpack", "Pens", "Pencils", "Highlighters", 
		"Notebooks", "Stapler", "Scissors", "Printer Paper", "Sticky Notes", 
		"Dry Erase Markers", "Desk Set", "Computer Mouse",
		"Misc"
	],
	"Decor": ["Throw Rug", "Throw Pillows", "Clip Lamps", "Desk Lamp", "Floor Lamp", "Lounge Seating", "Mirror", "Picture Frames", "String Lights", "Tension Rod", "Vanity Mirror", "Window Curtains", "Wall Safe Adhesive", "Dry Erase/Bulletin Board", "Standing Fan", "Bed Fan/Clip Fan", "Doorstopper", "Lap Desk", "Room Decor", "Faux Plants", "Misc"],
	"Health & Personal Care": ["Hair Dryer", "Styling Tools", "Hair Removal", "First Aid Kit", "Pain Reliever", "Sewing Kit", "Exercise Equipment", "Humidifier", "Diffuser", "Diffuser Oils", "Air Purifier", "Noise Machine", "Tool Set", "Misc"],
	"Kitchen": ["Microwave", "Refrigerator", "Personal Refrigerator", "Coffee Maker", "Rice Cooker", "Cooking Tools", "Smoothie Maker", "Air Fryer", "Dishes", "Silverware", "Water Bottles", "Travel Mug", "Can Opener", "Water Filtering", "Mugs", "Snack Bundles", "Food Storage", "Pots/Pans", "Cooking Utensils", "Misc"],
	"Storage & Organization": ["Bedside Shelf/Caddy", "Closet Organization", "Shoe Storage/Racks", "Hangers", "Trunk/Storage", "Underbed Storage", "Drawers", "Over Door Organization", "Rolling Cart", "Desk Organizer", "Wall Key Organizer", "Moving Bags", "Luggage", "Misc"]
};

const Catalogue = ({ onAddItems, isFromRegistry }) => {
	const SERVER_URL = API_URL;

	const db = getFirestore(app);
	const { headerTranslateY, onScroll } = useScrollHide('scrollView');
	const scrollViewRef = useRef();
	// In-memory cache: category name -> items[]. Persists for the life of the component
	// so clicking between categories is instant after the first load.
	const catalogueCacheRef = useRef({});
	const [catalogueItems, setCatalogueItems] = useState([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [viewCategories, setViewCategories] = useState(true);
	// loading flag when add button is pressed to prevent double clicks
	const [isAdding, setIsAdding] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	let itemsPerPage = 12;
	// Use a paginated size when opened from the Registry modal so pagination is shown
	if (isFromRegistry) itemsPerPage = 12;
	const [totalItemsCount, setTotalItemsCount] = useState(0);
	const totalPages = Math.ceil(totalItemsCount / itemsPerPage);
	const [selectedSubCategory, setSelectedSubCategory] = useState(null);
	const [visibilitySettings, setVisibilitySettings] = useState({
		hidePrice: false,
		hideImages: false,
		hideAll: false
	});
	const [amazonSearchQuery, setAmazonSearchQuery] = useState("");
	const [amazonSearchResults, setAmazonSearchResults] = useState([]);
	const [isSearchingAmazon, setIsSearchingAmazon] = useState(false);
	const [isLoadingItems, setIsLoadingItems] = useState(false);
	const [categories] = useState(VALID_SEARCH_INDICES);
	const [mainSearchQuery, setMainSearchQuery] = useState("");
	const [showSearchResults, setShowSearchResults] = useState(false);
	// Retry modal: shown when the server could not fetch enough metadata for a pasted URL
	const [showRetryModal, setShowRetryModal] = useState(false);
	const [retryUrl, setRetryUrl] = useState(null);
	const [retryAttempt, setRetryAttempt] = useState(0);

	const navigation = useNavigation();
	const route = useRoute();
	const { isFromLandingPage, isFromHub } = route.params || {}; // Get the parameter from the route

	const handleCategoryChange = useCallback((value) => {
		setSelectedCategory(value);
		setCurrentPage(1);
	}, []);

	const handleBackToLanding = () => {
		navigation.navigate('My Tassel - Welcome'); // Adjust 'LandingPage' to your actual landing page route name
	};

	const handleBackToHub = () => {
		navigation.navigate('Hub'); // Adjust 'Hub' to your actual hub page route name
	};

	useEffect(() => {
		let cancelled = false;

		const fetchCatalogueItems = async () => {
			const cacheKey = selectedCategory || '__ALL__';

			// Serve from cache immediately when available so switching categories feels instant
			if (catalogueCacheRef.current[cacheKey]) {
				const cached = catalogueCacheRef.current[cacheKey];
				const filtered = searchQuery
					? cached.filter(item => (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
					: cached;
				setCatalogueItems(filtered);
				setIsLoadingItems(false);
				return;
			}

			setIsLoadingItems(true);
			try {
				console.log("📡 fetchCatalogueItems called", { selectedCategory, isFromRegistry, searchQuery });
				// If a category is selected, fetch only that category; otherwise fetch all items
				let q;
				if (selectedCategory) {
					q = query(collection(db, "catalogueItems"), where("category", "==", selectedCategory));
				} else {
					q = query(collection(db, "catalogueItems"));
				}

				const querySnapshot = await getDocs(q);
				if (cancelled) return; // ignore stale response
				console.log("📡 querySnapshot size:", querySnapshot.size);
				const allItems = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

				// Cache the full (unfiltered) list for this category
				catalogueCacheRef.current[cacheKey] = allItems;

				const items = searchQuery
					? allItems.filter(item => (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
					: allItems;

				console.log("📡 items length after filter:", items.length);
				setCatalogueItems(items);
			} catch (err) {
				if (!cancelled) console.error('Error fetching catalogue items:', err);
			} finally {
				if (!cancelled) setIsLoadingItems(false);
			}
		};

		// Fetch when a category is selected or when opened as part of the Registry modal
		if (selectedCategory || isFromRegistry) {
			fetchCatalogueItems();
		}

		return () => {
			cancelled = true;
		};
	}, [selectedCategory, searchQuery, db, isFromRegistry]);

	useEffect(() => {
		const fetchVisibilitySettings = async () => {
			try {
				const docRef = doc(db, "maintenance", "catalogueVisibility");
				const docSnap = await getDoc(docRef);

				if (docSnap.exists()) {
					setVisibilitySettings(docSnap.data());
				}
			} catch (error) {
				
			}
		};

		fetchVisibilitySettings();
	}, []);

	// Prefetch all category items in the background so switching categories is instant.
	// Runs once on mount; each category is fetched only if not already cached.
	useEffect(() => {
		let cancelled = false;

		const prefetchAll = async () => {
			for (const category of VALID_SEARCH_INDICES) {
				if (cancelled) return;
				if (catalogueCacheRef.current[category]) continue;
				try {
					const q = query(collection(db, "catalogueItems"), where("category", "==", category));
					const snap = await getDocs(q);
					if (cancelled) return;
					const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
					catalogueCacheRef.current[category] = items;
				} catch (err) {
					// Silently ignore — the on-demand fetch will handle errors when user clicks
				}
			}
		};

		prefetchAll();
		return () => { cancelled = true; };
	}, [db]);

	const handleSelectCategory = useCallback((category) => {
		// Clear previous category's items immediately so we don't flash stale results
		setCatalogueItems([]);
		setAmazonSearchResults([]);
		setTotalItemsCount(0);
		setIsLoadingItems(true);
		setSelectedCategory(category);
		// Clear any previously selected subcategory so items for the new category show up
		setSelectedSubCategory(null);
		setViewCategories(false);
		setCurrentPage(1);
	}, []);

	useEffect(() => {
		const validItems = catalogueItems.filter(item => {
			if (!item) return false;
			const name = item.name || item.title;
			if (!name || typeof name !== 'string') return false;
			if (name.trim().toLowerCase().startsWith('amazon product (')) return false;
			if (!item.price) return false;
			if (!item.image && !item.imageUrl) return false;
			return true;
		});
		const filteredCount = selectedSubCategory
			? validItems.filter(item => item.subCategory === selectedSubCategory).length
			: validItems.length;
		setTotalItemsCount(filteredCount + amazonSearchResults.length);
	}, [catalogueItems, selectedSubCategory, amazonSearchResults]);

	const handleSelectSubCategory = useCallback((subCategory) => {
		setSelectedSubCategory((prev) => (prev === subCategory ? null : subCategory));
		setCurrentPage(1);
	}, []);

	const handleBackToCategories = () => {
		setViewCategories(true);
		// Optionally reset selected category here if needed
	};

	const handleSelectItem = (item) => {
		const isItemSelected = selectedItems.some((selectedItem) => selectedItem.id === item.id);
		if (isItemSelected) {
			setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id !== item.id));
		} else {
			const registryCandidate = toRegistryCandidate(item);
			setSelectedItems([...selectedItems, registryCandidate]);

			// Track item view when user selects/shows interest in an item
			try {
				AnalyticsManager.trackRegistryItem('registry_item_viewed', {
					id: item.id,
					name: item.name,
					category: item.category,
					subCategory: item.subCategory,
					price: item.price,
					url: registryCandidate.url,
					image: item.image
				});
			} catch (trackingError) {

				// Don't block the main process if tracking fails
			}
		}
		// Trigger a visual feedback update, e.g., by changing the state that affects the item's style
	};

	const truncateDescription = (description, wordLimit = 15) => {
		// Guard against null/undefined/non-string descriptions
		if (!description || typeof description !== 'string') return '';

		// First, trim to ensure there's no leading/trailing whitespace
		let cleanedDescription = description.trim();

		// Define the prefix to look for, in a case-insensitive manner
		const aboutThisItemPrefix = "About this item";

		// Use a regex to replace "About this item" and any number of following spaces with an empty string
		cleanedDescription = cleanedDescription.replace(new RegExp(`^${aboutThisItemPrefix}\\s*`, 'i'), '');

		// Split the remaining description into words, considering any amount of whitespace as a single splitter
		const words = cleanedDescription.split(/\s+/);

		// Truncate the description to the word limit, if necessary
		if (words.length > wordLimit) {
			return words.slice(0, wordLimit).join(" ") + "...";
		}

		// console.log(cleanedDescription)

		return cleanedDescription;
	};

	const truncateTitle = (title, wordLimit = 6) => {
		if (!title || typeof title !== 'string') return '';
		const words = title.split(" ");
		if (words.length > wordLimit) {
			return words.slice(0, wordLimit).join(" ") + "...";
		}
		return title;
	};

	// An item is considered "incomplete" (and should not be shown) when its metadata
	// failed to scrape – e.g. placeholder name like "Amazon Product (ASIN)" or missing
	// price/image. These slipped into Firestore from earlier preview-fetch failures.
	const isCompleteItem = (item) => {
		if (!item) return false;
		const name = item.name || item.title;
		if (!name || typeof name !== 'string') return false;
		if (name.trim().toLowerCase().startsWith('amazon product (')) return false;
		if (!item.price) return false;
		if (!item.image && !item.imageUrl) return false;
		return true;
	};

	const paginatedItems = useCallback(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;

		const validCatalogue = catalogueItems.filter(isCompleteItem);
		const filteredCatalogue = selectedSubCategory
			? validCatalogue.filter(item => item.subCategory === selectedSubCategory)
			: validCatalogue;

		const combinedItems = [
			...amazonSearchResults.map(item => ({
				id: item.asin,
				name: item.title,
				price: item.price,
				image: item.imageUrl,
				url: item.affiliateUrl,
				description: item.description,
				category: selectedCategory,
				subCategory: selectedSubCategory,
				isAmazonItem: true,
				relevanceScore: calculateRelevanceScore(item, searchQuery)
			})),
			...filteredCatalogue.map(item => ({
				...item,
				relevanceScore: calculateRelevanceScore(item, searchQuery)
			}))
		];

		const sortedItems = combinedItems.sort((a, b) => b.relevanceScore - a.relevanceScore);
		return sortedItems.slice(startIndex, startIndex + itemsPerPage);
	}, [amazonSearchResults, catalogueItems, currentPage, itemsPerPage, searchQuery, selectedCategory, selectedSubCategory]);

	const calculateRelevanceScore = (item, query) => {
		if (!query) return 0;
		
		const searchTerms = query.toLowerCase().split(' ');
		let score = 0;
		
		// Check title/name matches
		const titleWords = (item.name || item.title || '').toLowerCase();
		searchTerms.forEach(term => {
			if (titleWords.includes(term)) {
				score += 10; // Higher weight for title matches
			}
		});
		
		// Check description matches
		const description = (item.description || '').toLowerCase();
		searchTerms.forEach(term => {
			if (description.includes(term)) {
				score += 5; // Lower weight for description matches
			}
		});
		
		// Boost score for exact category/subcategory matches
		if (item.category?.toLowerCase().includes(query.toLowerCase())) {
			score += 8;
		}
		if (item.subCategory?.toLowerCase().includes(query.toLowerCase())) {
			score += 8;
		}
		
		return score;
	};

	const handleNextPage = () => {
		if (currentPage < totalPages) {
			setCurrentPage((prevCurrentPage) => prevCurrentPage + 1);
		}
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage((prevCurrentPage) => prevCurrentPage - 1);
		}
	};

	const handleAddItems = async () => {
	if (selectedItems.length === 0) {
		Alert.alert("No items selected", "Please select items to add.");
		return;
	}

	// prevent duplicate submissions
	if (isAdding) return;

	setIsAdding(true);
	try {
		await onAddItems(selectedItems);
		setSelectedItems([]); // Clear selected items after adding
	} catch (err) {
		// optionally show error
		console.error("Error adding items from catalogue:", err);
	} finally {
		setIsAdding(false);
		}
};

	const handleSearch = useCallback(async (query) => {
		if (!query.trim() || query.length < 3) return;

		console.log("🔍 Searching Amazon for:", query);
		setIsSearchingAmazon(true);
		try {
			const response = await fetch(`${SERVER_URL}/amazon-search`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					query: query.trim(),
					category: selectedCategory || 'All'
				})
			});

			console.log("📡 Search response status:", response.status);

			if (response.status === 429) {
				const retryAfter = response.headers.get('retry-after') || 1;
				alert(`Too many requests. Please wait ${retryAfter} second${retryAfter > 1 ? 's' : ''} before trying again.`);
				return;
			}

			if (!response.ok) {
				const errorData = await response.json();
				console.error("❌ Search failed:", errorData);
				alert(errorData.details || errorData.message || 'Search failed');
				return;
			}

			const data = await response.json();
			console.log("✅ Search results:", data.items?.length || 0, "items");
			if (data.items && Array.isArray(data.items)) {
				setAmazonSearchResults(data.items);
				setCurrentPage(1);
			} else {
				throw new Error('No results found');
			}
		} catch (error) {
			console.error("❌ Search error:", error);
			alert(error.message || 'Failed to search Amazon products. Please try again.');
		} finally {
			setIsSearchingAmazon(false);
		}
	}, [selectedCategory, SERVER_URL]);

	// Helper: detect Amazon URLs and extract a readable title from the URL path
const isAmazonUrl = (str) => {
	if (!str) return false;
	try {
		const u = new URL(str);
		return u.hostname.includes('amazon.') || str.includes('amzn.to') || str.includes('a.co');
	} catch (e) {
		return /amazon\.com|amzn\.to|a\.co/.test(str);
	}
};

const extractNameFromAmazonUrl = (str) => {
	try {
		let u;
		try {
			u = new URL(str);
		} catch (err) {
			// Try adding scheme if missing
			try { u = new URL('https://' + str); } catch (err2) { return 'Amazon Product'; }
		}

		const parts = u.pathname.split('/').filter(Boolean);
		if (parts.length) {
			// Typical URL: /{product-title}/dp/{ASIN}/... => first segment is often the human title
			let candidate = parts.find(p => !['dp', 'gp', 'product'].includes(p.toLowerCase()));
			if (!candidate) candidate = parts[0];
			candidate = candidate.replace(/[-_]+/g, ' ');
			try { candidate = decodeURIComponent(candidate); } catch (e) {}
			return candidate || 'Amazon Product';
		}
	} catch (e) {}
	return 'Amazon Product';
};

const handleMainSearch = useCallback(async (query) => {
	if (!query.trim() || query.length < 3) return;

	console.log("🔍 Main search for:", query);
	setIsSearchingAmazon(true);

	// If the user pasted an Amazon URL, try to fetch a preview (image, price, description) from our server
	if (isAmazonUrl(query.trim())) {
		const affiliateUrl = ensureAffiliateTag(query.trim());

		let timeoutId = null;
		try {
			const controller = new AbortController();
			timeoutId = setTimeout(() => controller.abort(), 25000); // 25 second timeout

			const resp = await fetch(`${SERVER_URL}/fetch-preview`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ url: query.trim() }),
				signal: controller.signal
			});

			clearTimeout(timeoutId);
			timeoutId = null;

			if (resp.ok) {
				const data = await resp.json();

				// Determine if the result is "complete" (has all three key fields)
				const hasTitle = !!(data.title && !data.title.startsWith('Amazon Product ('));
				const hasImage = !!data.imageUrl;
				const hasPrice = !!data.price;
				const isComplete = hasTitle && hasImage && hasPrice;

				if (!isComplete) {
					console.warn('⚠️ Preview fetch returned incomplete metadata:', { hasTitle, hasImage, hasPrice });
					// Do NOT add to registry – show retry modal instead
					setRetryUrl(query.trim());
					setRetryAttempt(prev => prev + 1);
					setShowRetryModal(true);
					setIsSearchingAmazon(false);
					return;
				}

				const previewItem = {
					id: `amazon-${Date.now()}`,
					name: data.title,
					price: data.price,
					image: data.imageUrl,
					url: affiliateUrl,
					affiliateUrl: affiliateUrl,
					description: data.description || '',
					isAmazonItem: true,
					category: selectedCategory || 'Amazon',
					subCategory: selectedSubCategory || null,
				};

				if (isFromRegistry && typeof onAddItems === 'function') {
					try {
						await onAddItems([previewItem]);
						setMainSearchQuery("");
						Alert.alert('Added!', 'Product added to your registry.');
					} catch (err) {
						console.error('Failed to add item from URL:', err);
						Alert.alert('Error', 'Failed to add product. Please try again.');
					} finally {
						setIsSearchingAmazon(false);
						setSelectedItems([]);
					}
					return;
				} else {
					setAmazonSearchResults([{
						asin: previewItem.id,
						title: previewItem.name,
						price: previewItem.price,
						imageUrl: previewItem.image,
						affiliateUrl: previewItem.affiliateUrl,
						description: previewItem.description
					}]);
					setSelectedItems([previewItem]);
					setShowSearchResults(true);
					setViewCategories(false);
					setCurrentPage(1);
					setIsSearchingAmazon(false);
					return;
				}
			} else {
				let errorData = {};
				let errorText = '';
				try {
					errorText = await resp.text();
					try { errorData = errorText ? JSON.parse(errorText) : {}; } catch (e) { errorData = { details: errorText }; }
				} catch (e) {}

				console.error(`❌ Preview fetch returned status ${resp.status}:`, errorData);
				// Show retry modal for any non-OK status
				setRetryUrl(query.trim());
				setRetryAttempt(prev => prev + 1);
				setShowRetryModal(true);
				setIsSearchingAmazon(false);
				return;
			}
		} catch (err) {
			if (timeoutId) clearTimeout(timeoutId);
			console.error('❌ Preview fetch failed:', err.name === 'AbortError' ? 'Timeout (25s)' : err.message);
			// Show retry modal on any error (timeout, network, etc.)
			setRetryUrl(query.trim());
			setRetryAttempt(prev => prev + 1);
			setShowRetryModal(true);
			setIsSearchingAmazon(false);
			return;
		}
	}

	// Non-URL: perform keyword search
	try {
		const response = await fetch(`${SERVER_URL}/amazon-search`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ query: query.trim(), category: 'All' })
		});

		console.log("📡 Main search response status:", response.status);

		if (response.status === 429) {
			const retryAfter = response.headers.get('retry-after') || 1;
			Alert.alert('Too Many Requests', `Please wait ${retryAfter} second${retryAfter > 1 ? 's' : ''} before trying again.`);
			return;
		}

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			Alert.alert('Search Failed', errorData.details || errorData.message || 'An error occurred. Please try again.');
			return;
		}

		const data = await response.json();
		if (data.items && Array.isArray(data.items)) {
			setAmazonSearchResults(data.items);
			setShowSearchResults(true);
			setViewCategories(false);
			setCurrentPage(1);
		} else {
			Alert.alert('No Results', 'No products found for your search.');
		}
	} catch (error) {
		console.error("❌ Main search error:", error);
		Alert.alert('Search Error', 'Failed to search products. Please check your connection and try again.');
	} finally {
		setIsSearchingAmazon(false);
	}
}, [SERVER_URL, selectedCategory, selectedSubCategory, isFromRegistry, onAddItems]);

	const handleRetryPreview = useCallback(async () => {
		if (!retryUrl) return;
		setShowRetryModal(false);
		// Re-run the main search, which will increment retryAttempt and show the modal again if still failing
		await handleMainSearch(retryUrl);
	}, [retryUrl, handleMainSearch]);

	const renderSubCategories = (category, inline = false) => {
		const containerStyles = [
			styles.subCategoryListContainer,
			isFromRegistry && styles.subCategoryListContainerCompact,
			inline && styles.subCategoryListContainerInline,
			inline && isFromRegistry && styles.subCategoryListContainerInlineCompact
		];

		// Only show subcategories that actually have valid items in the loaded catalogue
		const availableSubs = new Set(
			catalogueItems
				.filter(item => {
					if (!item) return false;
					const name = item.name || item.title;
					if (!name || typeof name !== 'string') return false;
					if (name.trim().toLowerCase().startsWith('amazon product (')) return false;
					if (!item.price) return false;
					if (!item.image && !item.imageUrl) return false;
					return true;
				})
				.map(item => item.subCategory)
				.filter(Boolean)
		);
		const visibleSubCategories = (subCategories[category] || [])
			.filter(sub => availableSubs.has(sub));

		// While items are still loading, fall back to showing all so the bar isn't empty
		const subsToRender = (isLoadingItems && availableSubs.size === 0)
			? (subCategories[category] || [])
			: visibleSubCategories;

		return (
			<View style={containerStyles}>
				{subsToRender.map((subCategory) => (
					<Pressable
						key={subCategory}
						style={({ hovered }) => [
							styles.subCategoryContainer,
							isFromRegistry && styles.subCategoryContainerCompact,
							inline && styles.subCategoryContainerInline,
							inline && isFromRegistry && styles.subCategoryContainerInlineCompact,
							hovered && styles.subCategoryContainerHover,
							selectedSubCategory === subCategory && styles.subCategoryContainerSelected
						]}
						onPress={() => handleSelectSubCategory(subCategory)}
					>
						<Text style={({ hovered }) => [
							styles.subCategoryText,
							isFromRegistry && styles.subCategoryTextCompact,
							inline && styles.subCategoryTextInline,
							inline && isFromRegistry && styles.subCategoryTextInlineCompact,
							hovered && styles.subCategoryTextHover
						]}>
							{subCategory}
						</Text>
					</Pressable>
				))}
			</View>
		);
	};

	const BackButton = ({ onPress, text }) => (
		<Pressable
			style={styles.backButton}
			onPress={onPress}
		>
			<Text style={styles.backButtonText}>{text}</Text>
		</Pressable>
	);

	const renderItem = ({ item }) => (
		<View style={styles.categoryContainer}>
			<Pressable
				onPress={() => handleSelectCategory(item)}
				style={({ hovered }) => [
					styles.categoryButton,
					hovered && styles.categoryContainerHover
				]}
			>
				<Image 
					source={categoryImages[item]} 
					style={styles.categoryImage} 
					resizeMode="cover"
				/>
				<View style={styles.textContainer}>
					<Text style={styles.categoryTitle}>{item}</Text>
				</View>
			</Pressable>
		</View>
	);

	const renderPaginationControls = () => {
		const totalPages = Math.ceil(totalItemsCount / itemsPerPage);
		const maxPagesToShow = 5;
		let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
		let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

		// Adjust start page if we're near the end
		if (endPage - startPage + 1 < maxPagesToShow) {
			startPage = Math.max(1, endPage - maxPagesToShow + 1);
		}

		const pageNumbers = [];
		for (let i = startPage; i <= endPage; i++) {
			pageNumbers.push(i);
		}

		return (
			<View style={[styles.paginationContainer, isFromRegistry && styles.paginationContainerCompact]}>
				<Pressable 
					onPress={() => setCurrentPage(1)} 
					disabled={currentPage === 1}
					style={[styles.paginationButton, isFromRegistry && styles.paginationButtonCompact, currentPage === 1 && styles.paginationButtonDisabled]}
				>
					<Text style={[styles.paginationText, isFromRegistry && styles.paginationTextCompact]}>First</Text>
				</Pressable>
				
				<Pressable 
					onPress={handlePreviousPage} 
					disabled={currentPage <= 1}
					style={[styles.paginationButton, isFromRegistry && styles.paginationButtonCompact, currentPage <= 1 && styles.paginationButtonDisabled]}
				>
					<Text style={[styles.paginationText, isFromRegistry && styles.paginationTextCompact]}>Previous</Text>
				</Pressable>

				{pageNumbers.map((pageNum) => (
					<Pressable
						key={pageNum}
						onPress={() => setCurrentPage(pageNum)}
						style={[
							styles.paginationButton,
							isFromRegistry && styles.paginationButtonCompact,
							currentPage === pageNum && styles.paginationButtonActive
						]}
					>
						<Text style={[
							styles.paginationText,
							isFromRegistry && styles.paginationTextCompact,
							currentPage === pageNum && styles.paginationTextActive
						]}>
							{pageNum}
						</Text>
					</Pressable>
				))}

				<Pressable 
					onPress={handleNextPage} 
					disabled={currentPage >= totalPages}
					style={[styles.paginationButton, isFromRegistry && styles.paginationButtonCompact, currentPage >= totalPages && styles.paginationButtonDisabled]}
				>
					<Text style={[styles.paginationText, isFromRegistry && styles.paginationTextCompact]}>Next</Text>
				</Pressable>
				
				<Pressable 
					onPress={() => setCurrentPage(totalPages)} 
					disabled={currentPage === totalPages}
					style={[styles.paginationButton, isFromRegistry && styles.paginationButtonCompact, currentPage === totalPages && styles.paginationButtonDisabled]}
				>
					<Text style={[styles.paginationText, isFromRegistry && styles.paginationTextCompact]}>Last</Text>
				</Pressable>
			</View>
		);
	};

	return (
		<View style={[styles.container, isFromRegistry && styles.registryContainer]}>
			{/* Only show background image when not from registry */}
			{!isFromRegistry && (
				<>
					<Image 
						source={require('../../assets/Landing Background.png')}
						style={styles.backgroundImage}
						resizeMode="cover"
					/>
					<View style={styles.blurOverlay} />
				</>
			)}
			
			{/* Add HeaderBar for standalone version with better visibility */}
			{!isFromRegistry && (
				<View style={styles.headerBarContainer}>
					<HeaderBar isFixed={false} headerTranslateY={headerTranslateY} />
				</View>
			)}
			
			{isFromLandingPage && (
				<BackButton 
					onPress={handleBackToLanding} 
					text="Back to Landing" 
				/>
			)}
			{isFromHub && (
				<BackButton 
					onPress={handleBackToHub} 
					text="Back to Hub" 
				/>
			)}
			
			{/* Content container with proper constraints */}
			<View style={!isFromRegistry ? styles.standaloneContent : styles.registryContent}>
				{/* Disclaimer at the top */}
				<View style={[
					styles.disclaimerContainer, 
					isFromRegistry && isMobile && {
						marginTop: 8,
						marginBottom: 8,
						paddingHorizontal: 8
					}
				]}>
					<Text style={styles.disclaimer}>Disclaimer: As an Amazon Associate MyTassel earns commissions from qualifying purchases on our site.</Text>
				</View>

				{viewCategories ? (
				<View style={[
					styles.categoriesWrapper,
					isFromRegistry && {
						flex: 1,
						height: '100%'
					}
				]}>
					<View style={[
						styles.searchContainer,
						isFromRegistry && isMobile && {
							marginVertical: 8,
							width: "98%"
						}
					]}>
					<View style={styles.searchInputContainer}>
						<TextInput
							style={styles.searchInput}
							placeholder="Paste Amazon product URL here..."
							value={mainSearchQuery}
							onChangeText={setMainSearchQuery}
						/>
					</View>
					<Pressable
						style={[
							styles.searchButton,
							(!mainSearchQuery.trim() || mainSearchQuery.length < 10) && styles.searchButtonDisabled
						]}
						onPress={() => handleMainSearch(mainSearchQuery)}
						disabled={!mainSearchQuery.trim() || mainSearchQuery.length < 10 || isSearchingAmazon}
					>
						<Text style={styles.searchButtonText}>
							{isSearchingAmazon ? 'Loading...' : 'Add Product'}
						</Text>
					</Pressable>
				</View>

					{categories && categories.length > 0 ? (
					<FlatList
						data={categories}
						renderItem={renderItem}
						keyExtractor={(item) => item}
						numColumns={isMobile ? 2 : 4}
						key={`cols-${isMobile ? 2 : 4}`}
						columnWrapperStyle={styles.columnWrapper}
						style={[
							isFromRegistry && {
								flex: 1,
								height: '100%'
							}
						]}
						contentContainerStyle={[
							styles.innerContainer,
							isFromRegistry && isMobile && {
								paddingHorizontal: 8,
								paddingBottom: 15,
								paddingTop: 5,
								flexGrow: 1
							}
						]}
						showsVerticalScrollIndicator={isFromRegistry}
						initialNumToRender={8}
						maxToRenderPerBatch={8}
						windowSize={5}
						scrollEnabled={true}
					/>
					) : (
						<Text style={{textAlign: 'center', marginTop: 20}}>Loading categories...</Text>
					)}
				</View>
			) : (
				<View style={[styles.itemsContainer, isFromRegistry && styles.registryItemsContainer]}>
{selectedCategory && (
				<View style={[styles.topRow, isFromRegistry && styles.topRowCompact]}>
					<View style={[styles.controlButtonsContainer, isFromRegistry && styles.controlButtonsContainerCompact]}>
						<Pressable style={[styles.backToCategoriesButtonMobile, isFromRegistry && styles.backToCategoriesButtonMobileCompact]} onPress={() => {
							setViewCategories(true);
							setShowSearchResults(false);
							setMainSearchQuery("");
							setSelectedCategory(null);
						}}>
							<Text style={[styles.backButtonText, isFromRegistry && styles.backButtonTextCompact]}>Back to Categories</Text>
						</Pressable>
						
						<Pressable
					style={[
						styles.addButtonMobile,
						isFromRegistry && styles.addButtonMobileCompact,
						(isAdding || selectedItems.length === 0) && styles.addButtonDisabled
					]}
					onPress={handleAddItems}
					disabled={isAdding || selectedItems.length === 0}
				>
							{isAdding ? (
						<ActivityIndicator color="#fff" style={{ paddingVertical: 6 }} />
					) : (
						<Text style={[styles.addButtonText, isFromRegistry && styles.addButtonTextCompact]}>Add Selected Items</Text>
					)}
						</Pressable>
					</View>

					<View style={[styles.subCategoryRightWrapper, isFromRegistry && styles.subCategoryRightWrapperCompact]}>
						{renderSubCategories(selectedCategory, true)}
					</View>
				</View>
			)}

					<FlatList
						data={isLoadingItems ? [] : paginatedItems()}
						onScroll={onScroll}
						scrollEventThrottle={16}
						style={{ 
							width: '100%',
							flex: 1,
							...(isFromRegistry && {
								height: '100%',
								minHeight: 0
							})
						}}
						contentContainerStyle={{ 
							paddingBottom: isMobile ? (isFromRegistry ? 20 : 80) : 40,
							paddingHorizontal: isMobile ? 8 : 12,
							paddingTop: isFromRegistry ? (isMobile ? 5 : 10) : 0,
							flexGrow: 1
						}}
						showsVerticalScrollIndicator={isFromRegistry ? true : false}
						showsHorizontalScrollIndicator={false}
						scrollEnabled={true}
						nestedScrollEnabled={isFromRegistry}
						bounces={true}
						removeClippedSubviews={false}
						disableVirtualization={isFromRegistry}
						keyExtractor={(item, index) => item.id || index.toString()}
						ListEmptyComponent={
							isLoadingItems ? (
								<View style={styles.loadingContainer}>
									<ActivityIndicator size="large" color="#4c956c" />
									<Text style={styles.loadingText}>Loading items...</Text>
								</View>
							) : (
								<View style={styles.loadingContainer}>
									<Text style={styles.loadingText}>No items found.</Text>
								</View>
							)
						}
						renderItem={({ item }) => {
							const isSelected = selectedItems.find((selectedItem) => selectedItem.id === item.id);
							return (
								<View
									style={[
										styles.itemCard,
										isSelected ? styles.itemSelected : {},
									]}
								>
									{/* Selection indicator overlay */}
									{isSelected && (
										<View style={styles.selectionOverlay}>
											<View style={styles.checkmarkContainer}>
												<Text style={styles.checkmark}>✓</Text>
											</View>
										</View>
									)}
									<Pressable
										onPress={() => handleSelectItem(item)}
										style={styles.itemCardContent}
									>
									{!visibilitySettings.hideAll && !visibilitySettings.hideImages && item.image && (
										<Image source={{ uri: item.image }} style={styles.itemImage} />
									)}
									{!visibilitySettings.hideAll && (
										<View style={styles.itemTextContainer}>
											<Text style={styles.itemName}>{truncateTitle(item.name)}</Text>
											{!visibilitySettings.hidePrice && (
												<Text style={styles.itemPrice}>{`${item.price}`}</Text>
											)}
											{!isMobile && (
												<Text style={styles.itemDescription}>{truncateDescription(item.description, 15)}</Text>
											)}
											<Text style={styles.itemSubCategory}>{item.subCategory}</Text>
											{item.isAmazonItem && (
												<Text style={styles.amazonLabel}>Amazon Product</Text>
											)}
										</View>
									)}
								</Pressable>
								<Pressable
									style={styles.seeProductButton}
									onPress={() => {
										const urlToOpen = ensureAffiliateTag(item.affiliateUrl || item.url);
										// Use window.open for all devices but with additional fallback for mobile
										const newWindow = window.open(urlToOpen, '_blank', 'noopener,noreferrer');
										// Fallback for mobile if popup is blocked
										if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
											window.location.href = urlToOpen;
										}
									}}
								>
									<Text style={styles.seeProductButtonText}>View Product</Text>
								</Pressable>
							</View>
						);
						}}
						numColumns={isMobile ? 2 : 3}
						key={`cols-${isMobile ? 2 : 3}`}
					/>

					<View style={styles.paginationContainer}>
						{renderPaginationControls()}
					</View>

				</View>
			)}
			</View> {/* Close content container */}

		{/* Retry Modal – shown when product metadata could not be fetched */}
		{showRetryModal && (
			<View style={styles.retryModalOverlay}>
				<View style={styles.retryModalContent}>
					<Text style={styles.retryModalTitle}>Couldn't Fetch Product Details</Text>
					<Text style={styles.retryModalBody}>
						We weren't able to retrieve the full details for this product (image, price, or title). This can happen if Amazon is temporarily blocking previews. Please try again in a moment.
					</Text>
					<TouchableOpacity
						style={styles.retryButton}
						onPress={handleRetryPreview}
						disabled={isSearchingAmazon}
					>
						{isSearchingAmazon
							? <ActivityIndicator color="#fff" />
							: <Text style={styles.retryButtonText}>Try Again</Text>
						}
					</TouchableOpacity>
					<TouchableOpacity
						style={styles.retryCancelButton}
						onPress={() => { setShowRetryModal(false); setRetryUrl(null); }}
						disabled={isSearchingAmazon}
					>
						<Text style={styles.retryCancelButtonText}>Cancel</Text>
					</TouchableOpacity>
				</View>
			</View>
		)}
		</View>
	);
};

const fontFamilies = {
	Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		width: "100%",
		height: "100%",
		top: 0,
		position: 'relative',
		backgroundColor: 'transparent',
		overflow: 'visible',
	},
	registryContainer: {
		flex: 1,
	},
	standaloneContent: {
		width: isMobile ? "98%" : isTablet ? "96%" : "95%",
		alignSelf: "center",
		backgroundColor: "#fefde2",
		borderRadius: isMobile ? 15 : isTablet ? 18 : 20,
		marginTop: isMobile ? 5 : isTablet ? 8 : 10,
		marginBottom: isMobile ? 5 : isTablet ? 8 : 10,
		paddingHorizontal: isMobile ? 10 : isTablet ? 12 : 15,
		paddingVertical: isMobile ? 8 : isTablet ? 9 : 10,
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.25,
		shadowRadius: 3.84,
		elevation: 5,
	},
	registryContent: {
		width: '100%',
		flex: 1,
		alignSelf: 'center',
		padding: isMobile ? 8 : 12,
		backgroundColor: '#fefde2',
	},
	categoriesWrapper: {
		width: '100%',
		alignItems: 'center',
		flex: 1, // Allow it to expand in registry modal
	},
	headerBarContainer: {
		backgroundColor: 'rgba(76, 149, 108, 0.9)', // Dark green semi-transparent background
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.3,
		shadowRadius: 4,
		elevation: 8,
		zIndex: 1000,
		borderBottomWidth: 2,
		borderBottomColor: 'rgba(76, 149, 108, 1)',
	},
	backgroundImage: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: '100%',
		height: '100%',
	},
	blurOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(255, 255, 255, 0.8)',
		backdropFilter: 'blur(10px)',
	},
	innerContainer: {
		flexDirection: "row",
		flexWrap: "wrap",
		justifyContent: "center",
		alignItems: "flex-start",
		paddingHorizontal: isMobile ? 10 : 40,
		paddingTop: 0,
		paddingBottom: isMobile ? 20 : 30,
		width: "100%",
		gap: isMobile ? 10 : 20,
		flexGrow: 1, // Allow growth for proper scrolling
		minHeight: 'auto',
	},
	categoryContainer: {
		backgroundColor: "#ffffff",
		borderRadius: 15,
		elevation: 3,
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.2,
		shadowRadius: 4,
		width: isMobile ? "45%" : "22%",
		aspectRatio: 4/3, // More rectangular aspect ratio
		overflow: "hidden",
		position: "relative",
		margin: 0, // Remove margin since we're using gap in container
		minWidth: isMobile ? 120 : isSmallDesktop ? 280 : 350, // Ensure minimum width
		maxWidth: isMobile ? 140 : isSmallDesktop ? 250 : 350, // Ensure maximum width
		height: isMobile ? 200 : isSmallDesktop ? 400 : 500,
	},
	categoryButton: {
		width: '100%',
		height: '100%',
		alignItems: 'center',
		justifyContent: 'flex-end',
		position: 'relative',
	},
	categoryImage: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: '100%',
		height: '100%',
	},
	textContainer: {
		backgroundColor: "rgba(255, 255, 255, 0.95)",
		width: "100%",
		paddingVertical: isMobile ? 12 : 15,
		alignItems: "center",
		borderTopWidth: 1,
		borderTopColor: "rgba(0,0,0,0.1)",
	},
	categoryTitle: {
		fontSize: isMobile ? 16 : 22,
		fontWeight: "600",
		color: "#333",
		textAlign: "center",
		fontFamily: fontFamilies.Bodoni72Smallcaps,
		letterSpacing: 0.5,
	},
	itemsContainer: {
		marginTop: isMobile ? 5 : 10,
		flexDirection: "column",
		justifyContent: "flex-start",
		alignItems: "center",
		paddingHorizontal: 0,
		paddingBottom: 0,
		width: "100%",
	},
	registryItemsContainer: {
		flex: 1,
	},
	itemCard: {
		backgroundColor: "#ffffff",
		borderRadius: 16,
		padding: isMobile ? 12 : 20,
		marginVertical: isMobile ? 8 : 12,
		marginHorizontal: isMobile ? 6 : 12,
		shadowColor: "#000000",
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.1,
		shadowRadius: 8,
		elevation: 3,
		alignItems: "center",
		justifyContent: 'space-between',
		maxWidth: isMobile ? "45%" : "30%",
		minWidth: isMobile ? "45%" : "30%",
		minHeight: isMobile ? 280 : 420,
		transition: 'all 0.2s ease',
		overflow: 'hidden',
		border: '1px solid #f1f5f9',
	},
	itemCardContent: {
		flex: 1,
		alignItems: "center",
		justifyContent: "flex-start",
		width: "100%",
	},
	itemTextContainer: {
		alignItems: "center",
		justifyContent: "center",
		flex: 1,
		width: "100%",
	},
	itemSelected: {
		borderColor: "#4c956c",
		borderWidth: 3,
		backgroundColor: "#f0f9ff",
		transform: [{ scale: 1.02 }],
		shadowColor: "#4c956c",
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 8,
	},
	itemImage: {
		width: "100%",
		height: isMobile ? 140 : 200,
		resizeMode: "cover",
		borderRadius: 12,
		marginBottom: isMobile ? 8 : 12,
		backgroundColor: '#f8fafc',
	},
	itemName: {
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontSize: isMobile ? 14 : 16,
		fontWeight: "600",
		color: "#1e293b",
		textAlign: "center",
		marginBottom: isMobile ? 4 : 8,
		lineHeight: isMobile ? 20 : 22,
	},

	itemDescription: {
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontSize: isMobile ? 12 : 14,
		color: "#64748b",
		textAlign: "center",
		marginBottom: isMobile ? 6 : 10,
		lineHeight: isMobile ? 18 : 20,
	},

	itemPrice: {
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontSize: isMobile ? 14 : 16,
		fontWeight: "700",
		color: "#059669",
		textAlign: "center",
		marginBottom: isMobile ? 4 : 8,
	},
	backButton: {
		position: 'absolute',
		top: isMobile ? 5 : isSmallDesktop ? 10 : 20,
		left: isMobile ? 5 : isSmallDesktop ? 10 : 20,
		backgroundColor: '#4c956c',
		padding: 10,
		borderRadius: 5,
		zIndex: 1000,
	},
	controlButtonsContainer: {
		flexDirection: 'row',
		justifyContent: isMobile ? 'space-between' : 'center',
		alignItems: 'center',
		paddingHorizontal: 20,
		paddingVertical: 10,
		marginBottom: 10,
		width: isMobile ? '100%' : '60%',
		alignSelf: 'center',
		gap: isMobile ? 0 : 20,
	},
	backToCategoriesButtonMobile: {
		backgroundColor: "#6b7280",
		paddingVertical: isMobile ? 10 : 12,
		paddingHorizontal: isMobile ? 16 : 24,
		borderRadius: 10,
		flex: isMobile ? 1 : 0,
		marginRight: isMobile ? 8 : 0,
		alignItems: 'center',
		minWidth: isMobile ? 'auto' : 150,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	addButtonMobile: {
		backgroundColor: "#10b981",
		paddingVertical: isMobile ? 10 : 12,
		paddingHorizontal: isMobile ? 16 : 24,
		borderRadius: 10,
		flex: isMobile ? 1 : 0,
		marginLeft: isMobile ? 8 : 0,
		alignItems: 'center',
		minWidth: isMobile ? 'auto' : 150,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	// Compact variants for Registry modal
	backToCategoriesButtonMobileCompact: {
		backgroundColor: "#6b7280",
		paddingVertical: isMobile ? 8 : 10,
		paddingHorizontal: isMobile ? 12 : 16,
		borderRadius: isMobile ? 8 : 10,
		flex: isMobile ? 1 : 0,
		minWidth: isMobile ? 'auto' : 120,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.15s ease',
	},
	addButtonMobileCompact: {
		backgroundColor: "#10b981",
		paddingVertical: isMobile ? 8 : 10,
		paddingHorizontal: isMobile ? 12 : 16,
		borderRadius: isMobile ? 8 : 10,
		flex: isMobile ? 1 : 0,
		minWidth: isMobile ? 'auto' : 120,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.15s ease',
	},
	// when button is disabled (no items selected or in-progress)
	addButtonDisabled: {
		opacity: 0.6,
		cursor: 'not-allowed',
	},
	backButtonTextCompact: {
		fontSize: isMobile ? 12 : 13,
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
		color: '#ffffff',
	},
	addButtonTextCompact: {
		fontSize: isMobile ? 12 : 13,
		color: '#ffffff',
		fontWeight: '600',
	},
	backToCategoriesButton: {
		position: "absolute",
		top: isMobile ? 50 : 20,
		left: 20,
		backgroundColor: "#4c956c",
		paddingVertical: 10,
		paddingHorizontal: 20,
		borderRadius: 20,
		zIndex: 10,
	},
	backButtonText: {
		color: '#ffffff',
		fontSize: isMobile ? 13 : 15,
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
	},
	addButton: {
		position: "absolute",
		top: isMobile ? 0 : 20, 
		right: 20,
		backgroundColor: "#4c956c",
		paddingVertical: 10,
		paddingHorizontal: 20,
		borderRadius: 20,
		zIndex: 10,
	},
	addButtonText: {
		fontSize: isMobile ? 13 : 15,
		color: "#ffffff",
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
	},
	searchContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		width: isMobile ? "95%" : "80%",
		marginVertical: isMobile ? 16 : 24,
		alignSelf: 'center',
		gap: 12,
	},
	searchInputContainer: {
		flex: 1,
		flexDirection: 'row',
		marginRight: 10,
	},
	searchInput: {
		flex: 1,
		fontSize: isMobile ? 14 : 16,
		padding: isMobile ? 12 : 16,
		borderWidth: 1,
		borderColor: "#e2e8f0",
		borderRadius: 12,
		backgroundColor: "#ffffff",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#1e293b",
		shadowColor: "#000000",
		shadowOffset: { width: 0, height: 1 },
		shadowOpacity: 0.05,
		shadowRadius: 3,
		elevation: 1,
	},
	searchButton: {
		backgroundColor: "#4c956c",
		paddingVertical: isMobile ? 12 : 16,
		paddingHorizontal: isMobile ? 16 : 24,
		borderRadius: 12,
		justifyContent: 'center',
		alignItems: 'center',
		minWidth: isMobile ? 80 : 100,
		transition: 'all 0.2s ease',
		border: 'none',
		cursor: 'pointer',
	},
	searchButtonDisabled: {
		backgroundColor: "#94a3b8",
		opacity: 0.6,
	},
	searchButtonText: {
		color: "#ffffff",
		fontSize: isMobile ? 12 : 14,
		fontFamily: "system-ui, -apple-system, sans-serif",
		fontWeight: '600',
	},
	paginationContainer: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		paddingVertical: isMobile ? 5 : 8,
		gap: isMobile ? 5 : 10,
		width: '100%',
		marginHorizontal: isMobile ? 10 : 0,
		flexWrap: 'wrap', // Allow wrapping on mobile
	},
	paginationButton: {
		paddingHorizontal: isMobile ? 10 : 16,
		paddingVertical: isMobile ? 8 : 10,
		backgroundColor: '#4c956c',
		borderRadius: 8,
		minWidth: isMobile ? 36 : 44,
		alignItems: 'center',
		marginHorizontal: isMobile ? 3 : 4,
		border: 'none',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
	},
	paginationButtonDisabled: {
		backgroundColor: '#94a3b8',
		opacity: 0.6,
		cursor: 'not-allowed',
	},
	paginationButtonActive: {
		backgroundColor: '#2c6e3e',
		transform: 'scale(1.05)',
	},
	paginationText: {
		color: '#ffffff',
		fontSize: isMobile ? 12 : 16,
	},
	paginationTextActive: {
		fontWeight: 'bold',
	},
	// Compact pagination for Registry modal
	paginationContainerCompact: {
		paddingVertical: isMobile ? 4 : 6,
		gap: isMobile ? 4 : 6,
	},
	paginationButtonCompact: {
		paddingHorizontal: isMobile ? 8 : 10,
		paddingVertical: isMobile ? 6 : 8,
		minWidth: isMobile ? 28 : 36,
	},
	paginationTextCompact: {
		fontSize: isMobile ? 11 : 12,
	},
	seeProductButton: {
		marginTop: isMobile ? 8 : 12,
		paddingVertical: isMobile ? 8 : 12,
		paddingHorizontal: isMobile ? 12 : 20,
		backgroundColor: '#4c956c',
		borderRadius: isMobile ? 8 : 10,
		alignSelf: 'center',
		minWidth: isMobile ? 100 : 120,
		maxWidth: '90%',
		transition: 'all 0.2s ease',
		border: 'none',
		cursor: 'pointer',
	},
	seeProductButtonText: {
		color: '#ffffff',
		fontSize: isMobile ? 12 : 14,
		textAlign: 'center',
		fontWeight: '600',
		fontFamily: "system-ui, -apple-system, sans-serif",
	},
	disclaimer: {
		fontFamily: "Orpheus Pro",
		fontSize: isMobile ? 12 : 18,
		textAlign: "center",
		color: "#4C956C",
		marginBottom: isMobile ? 8 : 15,
		fontStyle: "italic",
	},
	disclaimerContainer: {
		justifyContent: "center",
		alignItems: "center",
		marginTop: 20,
		marginBottom: 20,
		paddingHorizontal: 20,
	},
	itemHover: {
		transform: [{ scale: 1.025 }],
	},
	subCategoryContainer: {
		backgroundColor: "#f1f5f9",
		borderRadius: isMobile ? 8 : 12,
		margin: isMobile ? 4 : 6,
		overflow: "hidden",
		border: '1px solid #e2e8f0',
		width: "auto",
		height: isMobile ? 36 : 44,
		paddingHorizontal: isMobile ? 12 : 16,
		paddingVertical: isMobile ? 8 : 10,
		alignItems: "center",
		justifyContent: "center",
		transition: 'all 0.2s ease',
		cursor: 'pointer',
	},
	subCategoryContainerHover: {
		backgroundColor: "#e2e8f0",
		borderColor: "#cbd5e1",
	},
	subCategoryContainerSelected: {
		backgroundColor: "#4c956c",
		borderColor: "#2c6e3e",
	},
	subCategoryText: {
		fontSize: isMobile ? 12 : 14,
		fontWeight: "500",
		textAlign: "center",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#475569",
		whiteSpace: 'nowrap',
	},
	subCategoryTextHover: {
		fontSize: isMobile ? 12 : 14,
		fontWeight: "600",
		textAlign: "center",
		fontFamily: "system-ui, -apple-system, sans-serif",
		color: "#334155",
		whiteSpace: 'nowrap',
	},
	subCategoryListContainer: {
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
		paddingVertical: isMobile ? 12 : 16,
		flexWrap: "wrap",
		gap: isMobile ? 8 : 12,
		width: "100%",
		paddingHorizontal: isMobile ? 16 : "10%",
		marginBottom: isMobile ? 16 : 20,
		marginHorizontal: isMobile ? 8 : 16,
	},
	// Compact subcategory styles to reduce vertical space (for Registry modal)
	subCategoryListContainerCompact: {
		paddingVertical: isMobile ? 2 : 6,
		flexWrap: "wrap",
		gap: isMobile ? 2 : 6,
		marginBottom: isMobile ? 2 : 6,
		paddingHorizontal: isMobile ? 4 : "4%",
		justifyContent: "center",
		alignItems: "center",
	},
	subCategoryContainerCompact: {
		height: isMobile ? 18 : 28,
		paddingHorizontal: isMobile ? 4 : 8,
		paddingVertical: isMobile ? 2 : 6,
		margin: isMobile ? 1 : 2,
		borderRadius: isMobile ? 4 : 8,
		backgroundColor: isMobile ? "#f8fafc" : "#f1f5f9",
		border: isMobile ? '1px solid #f1f5f9' : '1px solid #e2e8f0',
	},
	subCategoryTextCompact: {
		fontSize: isMobile ? 8 : 11,
		fontWeight: isMobile ? "400" : "500",
		color: isMobile ? "#64748b" : "#475569",
	},
	// Inline/Right-aligned variants for placing tags to the right of buttons
	subCategoryListContainerInline: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		alignItems: 'center',
		paddingVertical: isMobile ? 4 : 6,
		flexWrap: 'wrap',
		gap: isMobile ? 6 : 8,
		marginBottom: 0,
		paddingHorizontal: isMobile ? 6 : 12,
		width: isMobile ? '100%' : 'auto',
	},
	subCategoryListContainerInlineCompact: {
		paddingVertical: isMobile ? 1 : 4,
		gap: isMobile ? 2 : 6,
		paddingHorizontal: isMobile ? 2 : 8,
		marginBottom: 0,
		justifyContent: "center",
		alignItems: "center",
	},
	subCategoryContainerInline: {
		height: isMobile ? 22 : 26,
		paddingHorizontal: isMobile ? 6 : 8,
		paddingVertical: isMobile ? 3 : 4,
		margin: isMobile ? 2 : 4,
		borderRadius: isMobile ? 6 : 8,
	},
	subCategoryContainerInlineCompact: {
		height: isMobile ? 16 : 24,
		paddingHorizontal: isMobile ? 3 : 6,
		paddingVertical: isMobile ? 1 : 3,
		margin: isMobile ? 0.5 : 2,
		backgroundColor: isMobile ? "#f8fafc" : "#f1f5f9",
		border: isMobile ? '1px solid #f1f5f9' : '1px solid #e2e8f0',
	},
	subCategoryTextInline: {
		fontSize: isMobile ? 8 : 12,
		textAlign: 'center',
		color: isMobile ? "#64748b" : "#475569",
		fontWeight: isMobile ? "400" : "500",
	},
	subCategoryTextInlineCompact: {
		fontSize: isMobile ? 7 : 11,
		textAlign: 'center',
		color: isMobile ? "#64748b" : "#475569",
		fontWeight: isMobile ? "400" : "500",
	},
	// Top row wrapper to place buttons left and tags right
	topRow: {
		width: '100%',
		flexDirection: isMobile ? 'column' : 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: isMobile ? 8 : 12,
		paddingHorizontal: isMobile ? 8 : 16,
		marginBottom: isMobile ? 6 : 12,
	},
	topRowCompact: {
		paddingHorizontal: isMobile ? 6 : 12,
		marginBottom: isMobile ? 4 : 8,
	},
	subCategoryRightWrapper: {
		flex: 1,
		alignItems: 'flex-end',
		justifyContent: 'center',
	},
	controlButtonsContainerCompact: {
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		paddingHorizontal: isMobile ? 8 : 12,
		paddingVertical: isMobile ? 6 : 8,
		width: isMobile ? '100%' : '30%', // Buttons column ~30% on desktop
		gap: isMobile ? 8 : 12,
		alignSelf: 'flex-start',
	},
	subCategoryRightWrapperCompact: {
		alignItems: isMobile ? 'center' : 'flex-end',
		paddingRight: isMobile ? 0 : 8,
		width: isMobile ? '100%' : '70%', // Tags column ~70% on desktop
	},
	amazonLabel: {
		fontSize: isMobile ? 12 : 14,
		color: '#4c956c',
		fontWeight: 'bold',
		marginTop: 5,
		fontFamily: "Orpheus Pro",
	},
	categoryPicker: {
		width: 150,
		marginLeft: 10,
	},
	columnWrapper: {
		justifyContent: 'center',
		gap: isMobile ? 10 : 20,
	},
	selectionOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(76, 149, 108, 0.1)',
		borderRadius: 16,
		zIndex: 10,
		pointerEvents: 'none',
	},
	checkmarkContainer: {
		position: 'absolute',
		top: 8,
		right: 8,
		width: isMobile ? 24 : 30,
		height: isMobile ? 24 : 30,
		backgroundColor: '#4c956c',
		borderRadius: isMobile ? 12 : 15,
		justifyContent: 'center',
		alignItems: 'center',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.25,
		shadowRadius: 3,
		elevation: 5,
	},
	checkmark: {
		color: 'white',
		fontSize: isMobile ? 16 : 18,
		fontWeight: 'bold',
		textAlign: 'center',
	},

	loadingContainer: {
		flex: 1,
		width: '100%',
		alignItems: 'center',
		justifyContent: 'center',
		paddingVertical: isMobile ? 40 : 80,
		gap: 12,
	},
	loadingText: {
		marginTop: 12,
		fontSize: isMobile ? 14 : 16,
		color: '#4c956c',
		fontFamily: 'system-ui, -apple-system, sans-serif',
		fontWeight: '500',
		textAlign: 'center',
	},

	// Retry Modal
	retryModalOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(0,0,0,0.55)',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 9999,
	},
	retryModalContent: {
		backgroundColor: '#fff',
		borderRadius: 16,
		padding: 28,
		marginHorizontal: 24,
		maxWidth: 420,
		width: '90%',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.3,
		shadowRadius: 8,
		elevation: 10,
	},
	retryModalTitle: {
		fontSize: isMobile ? 17 : 19,
		fontWeight: '700',
		color: '#1a1a1a',
		marginBottom: 12,
		textAlign: 'center',
	},
	retryModalBody: {
		fontSize: isMobile ? 14 : 15,
		color: '#555',
		lineHeight: 22,
		textAlign: 'center',
		marginBottom: 24,
	},
	retryButton: {
		backgroundColor: '#4c956c',
		borderRadius: 10,
		paddingVertical: 13,
		alignItems: 'center',
		marginBottom: 10,
	},
	retryButtonText: {
		color: '#fff',
		fontSize: 15,
		fontWeight: '600',
	},
	retryCancelButton: {
		borderRadius: 10,
		paddingVertical: 11,
		alignItems: 'center',
		borderWidth: 1,
		borderColor: '#ccc',
	},
	retryCancelButtonText: {
		color: '#555',
		fontSize: 15,
		fontWeight: '500',
	},
});

export default Catalogue;